import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  AlertCircle, 
  ListTodo, 
  Download,
  Search,
  Filter,
  Calendar,
  Users,
  Play,
  BarChart3,
  Target,
  Megaphone,
  ChevronUp,
  ChevronDown,
  Pencil
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch, API_BASE as API } from "../utils/api";
import { PersonalTaskEditDialog } from "../components/PersonalTaskEditDialog";
import { DashboardPMView } from "../components/DashboardPMView";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica",
  script_videomaker: "Script Videomaker",
  script_cliente: "Script Cliente",
  conferma_script: "Conferma Script",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio ADV",
  appuntamento_successivo: "Appuntamento Successivo",
  report_fine_mese: "Report Fine Mese",
  editing_video: "Editing Video",
  allineamento_strategico_tiktok: "Allineamento Strategico (TikTok)",
  call_settimanale_tiktok: "Call Settimanale (TikTok)",
  check_adv_tiktok: "Check ADV (TikTok)",
  check_affiliazioni_live_tiktok: "Check Affiliazioni e Live (TikTok)"
};

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const isPM = true; // Tutti vedono la vista raggruppata Oggi/Settimana/Mese
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [personalTasks, setPersonalTasks] = useState([]);
  const [pmStats, setPmStats] = useState([]);
  const [pmAnalytics, setPmAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSemaforo, setFilterSemaforo] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeKpi, setActiveKpi] = useState(null);
  const [showMissedTasks, setShowMissedTasks] = useState(false);
  
  // Accordion states for personal tasks
  const [showPendingTasks, setShowPendingTasks] = useState(true);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  
  // Edit personal task
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    fetchData();
    // Check payment reset on page load
    checkPaymentReset();
  }, []);

  const checkPaymentReset = async () => {
    try {
      await apiFetch("/admin/check-payment-reset");
    } catch (error) {
      // Silent fail
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, tasksRes, todayRes, personalRes] = await Promise.all([
        apiFetch("/tasks/stats"),
        apiFetch("/tasks"),
        apiFetch("/tasks/today"),
        apiFetch("/personal-tasks")
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (todayRes.ok) setTodayTasks(await todayRes.json());
      if (personalRes.ok) setPersonalTasks(await personalRes.json());

      // PM/Direttori: fetch team stats and analytics
      const isPmOrDir = user?.role === "pm" || user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || (user?.secondary_roles || []).includes("pm");
      if (isPmOrDir) {
        const [pmRes, analyticsRes] = await Promise.all([
          apiFetch("/dashboard/pm-stats"),
          apiFetch("/dashboard/pm-analytics")
        ]);
        if (pmRes.ok) setPmStats(await pmRes.json());
        if (analyticsRes.ok) setPmAnalytics(await analyticsRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiFetch("/export/excel");
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `smm_export_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Export completato!");
      }
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const response = await apiFetch(`/tasks/${taskId}/status?status=${newStatus}`, {
        method: "PATCH"
      });
      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const getSemaforoBadge = (semaforo, status) => {
    if (status === "completato") {
      return <Badge className="bg-slate-100 text-slate-600 border-slate-200">Completato</Badge>;
    }
    const styles = {
      verde: "badge-verde",
      giallo: "badge-giallo",
      rosso: "badge-rosso",
      scaduto: "badge-scaduto"
    };
    const labels = {
      verde: "In tempo",
      giallo: "Attenzione",
      rosso: "Oggi",
      scaduto: "Scaduto"
    };
    return <Badge className={`${styles[semaforo]} border`}>{labels[semaforo]}</Badge>;
  };

  // Filter tasks based on active KPI
  const getFilteredTasksByKpi = () => {
    if (!activeKpi) return [];
    
    return tasks.filter(task => {
      if (activeKpi === "verde") return task.semaforo === "verde" && task.status !== "completato";
      if (activeKpi === "giallo") return task.semaforo === "giallo" && task.status !== "completato";
      if (activeKpi === "rosso") return task.semaforo === "rosso" && task.status !== "completato";
      if (activeKpi === "scaduto") return task.semaforo === "scaduto" && task.status !== "completato";
      if (activeKpi === "total") return true;
      return false;
    });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      etichettaTask(task, TASK_TYPE_LABELS).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSemaforo = filterSemaforo === "all" || task.semaforo === filterSemaforo;
    const matchesStatus = filterStatus === "all" || task.status === filterStatus;
    return matchesSearch && matchesSemaforo && matchesStatus;
  });

  // Get overdue tasks
  const overdueTasks = tasks.filter(t => t.semaforo === "scaduto" && t.status !== "completato");
  
  // Get this week's tasks
  const now = new Date();
  const weekEnd = new Date(now);
  weekEnd.setDate(weekEnd.getDate() + 7);
  const weekTasks = tasks.filter(t => {
    const dueDate = new Date(t.due_date);
    return dueDate >= now && dueDate <= weekEnd && t.status !== "completato";
  });

  const handleKpiClick = (kpiType) => {
    setActiveKpi(activeKpi === kpiType ? null : kpiType);
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const kpiTasksList = getFilteredTasksByKpi();

  return (
    <div className="flex flex-col gap-6 lg:gap-7 animate-fade-in pb-5" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Benvenuto, {user?.name || "Utente"}
          </p>
        </div>
        <Button
          data-testid="export-btn"
          onClick={handleExport}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Esporta Excel
        </Button>
      </div>

      {/* KPI Cards - Clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        <Card 
          className={`kpi-card cursor-pointer transition-all ${activeKpi === "total" ? "ring-2 ring-indigo-500" : ""}`}
          onClick={() => handleKpiClick("total")}
          data-testid="kpi-total"
        >
          <CardContent className="p-4 lg:px-5 lg:py-4">
            <div className="flex items-center justify-between">
              <ListTodo className="w-6 h-6 text-indigo-600" />
              <span className="font-heading text-2xl font-bold">{stats?.total || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">Task Totali</p>
          </CardContent>
        </Card>

        <Card 
          className={`kpi-card border-l-4 border-l-emerald-500 cursor-pointer transition-all ${activeKpi === "verde" ? "ring-2 ring-emerald-500" : ""}`}
          onClick={() => handleKpiClick("verde")}
          data-testid="kpi-verde"
        >
          <CardContent className="p-4 lg:px-5 lg:py-4">
            <div className="flex items-center justify-between">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <span className="font-heading text-2xl font-bold">{stats?.verde || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">In Tempo</p>
          </CardContent>
        </Card>

        <Card 
          className={`kpi-card border-l-4 border-l-amber-500 cursor-pointer transition-all ${activeKpi === "giallo" ? "ring-2 ring-amber-500" : ""}`}
          onClick={() => handleKpiClick("giallo")}
          data-testid="kpi-giallo"
        >
          <CardContent className="p-4 lg:px-5 lg:py-4">
            <div className="flex items-center justify-between">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <span className="font-heading text-2xl font-bold">{stats?.giallo || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">Attenzione</p>
          </CardContent>
        </Card>

        <Card 
          className={`kpi-card border-l-4 border-l-red-500 cursor-pointer transition-all ${activeKpi === "rosso" ? "ring-2 ring-red-500" : ""}`}
          onClick={() => handleKpiClick("rosso")}
          data-testid="kpi-rosso"
        >
          <CardContent className="p-4 lg:px-5 lg:py-4">
            <div className="flex items-center justify-between">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <span className="font-heading text-2xl font-bold">{stats?.rosso || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">Oggi</p>
          </CardContent>
        </Card>

        <Card 
          className={`kpi-card border-l-4 border-l-zinc-500 cursor-pointer transition-all ${activeKpi === "scaduto" ? "ring-2 ring-zinc-500" : ""}`}
          onClick={() => handleKpiClick("scaduto")}
          data-testid="kpi-scaduto"
        >
          <CardContent className="p-4 lg:px-5 lg:py-4">
            <div className="flex items-center justify-between">
              <Clock className="w-6 h-6 text-zinc-500" />
              <span className="font-heading text-2xl font-bold">{stats?.scaduto || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">Scaduti</p>
          </CardContent>
        </Card>
      </div>

      {/* PM/Admin: Vista raggruppata per operativo */}
      {isPM ? (
        <DashboardPMView user={user} />
      ) : (
      <>
      {/* KPI Filtered Tasks */}
      {activeKpi && (
        <Card className="border border-slate-200 animate-fade-in" data-testid="kpi-tasks-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                {activeKpi === "total" && "Tutti i Task"}
                {activeKpi === "verde" && "Task In Tempo"}
                {activeKpi === "giallo" && "Task in Attenzione"}
                {activeKpi === "rosso" && "Task di Oggi"}
                {activeKpi === "scaduto" && "Task Scaduti"}
                {" "}({kpiTasksList.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setActiveKpi(null)}>
                Chiudi
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <TaskList 
              tasks={kpiTasksList} 
              onStatusChange={updateTaskStatus}
              getSemaforoBadge={getSemaforoBadge}
              navigate={navigate}
              showStatusButtons={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Tasks Tabs */}
      {!activeKpi && (
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
            <TabsTrigger value="today" data-testid="tab-today">
              Oggi ({todayTasks.length})
            </TabsTrigger>
            <TabsTrigger value="week" data-testid="tab-week">
              Settimana ({weekTasks.length})
            </TabsTrigger>
            <TabsTrigger value="overdue" data-testid="tab-overdue">
              Scaduti ({overdueTasks.length})
            </TabsTrigger>
            <TabsTrigger value="personal" data-testid="tab-personal">
              Personali ({personalTasks.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              Tutti
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-4">
            <TaskList 
              tasks={todayTasks} 
              onStatusChange={updateTaskStatus}
              getSemaforoBadge={getSemaforoBadge}
              navigate={navigate}
              showStatusButtons={true}
            />
          </TabsContent>

          <TabsContent value="week" className="mt-4">
            <TaskList 
              tasks={weekTasks} 
              onStatusChange={updateTaskStatus}
              getSemaforoBadge={getSemaforoBadge}
              navigate={navigate}
              showStatusButtons={true}
            />
          </TabsContent>

          <TabsContent value="overdue" className="mt-4">
            <TaskList 
              tasks={overdueTasks} 
              onStatusChange={updateTaskStatus}
              getSemaforoBadge={getSemaforoBadge}
              navigate={navigate}
              showStatusButtons={true}
            />
          </TabsContent>

          <TabsContent value="personal" className="mt-4">
            {personalTasks.length > 0 ? (
              <Card className="border border-indigo-200 overflow-hidden">
                {/* Header Accordion Principale */}
                <div className="bg-indigo-50 p-4 border-b border-indigo-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <ListTodo className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-indigo-800">TASK PERSONALI</h3>
                      <p className="text-sm text-indigo-600">
                        {personalTasks.filter(t => t.status !== "completato").length} da fare • {personalTasks.filter(t => t.status === "completato").length} completate
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sezione Task da Fare */}
                <div className="border-b border-indigo-100">
                  <div 
                    className="bg-amber-50 p-3 flex items-center justify-between cursor-pointer hover:bg-amber-100/50 transition-colors"
                    onClick={() => setShowPendingTasks(!showPendingTasks)}
                    data-testid="pending-tasks-accordion"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronUp className={`w-4 h-4 text-amber-600 transition-transform ${!showPendingTasks ? 'rotate-180' : ''}`} />
                      <span className="font-semibold text-amber-800">
                        Task da fare ({personalTasks.filter(t => t.status !== "completato").length})
                      </span>
                    </div>
                  </div>
                  
                  {showPendingTasks && (
                    <div className="p-3 space-y-2 bg-white">
                      {personalTasks.filter(t => t.status !== "completato").length > 0 ? (
                        personalTasks
                          .filter(t => t.status !== "completato")
                          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                          .map(task => (
                          <div 
                            key={task.task_id}
                            className={`p-3 border rounded-lg ${
                              new Date(task.due_date) < new Date() 
                                ? "bg-red-50 border-red-200"
                                : "bg-white border-slate-200 hover:border-indigo-200"
                            }`}
                            data-testid={`personal-task-${task.task_id}`}
                          >
                            {/* Badge Cliente in grassetto */}
                            <div className="mb-1">
                              <Badge className="bg-indigo-100 text-indigo-800 font-bold text-xs">
                                {task.client_name || "Cliente non assegnato"}
                              </Badge>
                            </div>
                            <div className="flex items-start justify-between">
                              <p className="font-medium flex-1">{task.title}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 shrink-0"
                                onClick={(e) => { e.stopPropagation(); setEditingTask(task); setShowEditDialog(true); }}
                                data-testid={`edit-personal-task-${task.task_id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(task.due_date).toLocaleDateString("it-IT")}
                              </Badge>
                              {task.smm_name && (
                                <Badge variant="secondary" className="text-xs">
                                  {task.smm_name}
                                </Badge>
                              )}
                              <Badge className={`text-xs ${
                                task.status === "in_corso"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-slate-100 text-slate-700"
                              }`}>
                                {task.status === "in_corso" ? "In corso" : "Da fare"}
                              </Badge>
                              {new Date(task.due_date) < new Date() && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  Scaduta
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          🎉 Nessuna task da completare
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Sezione Task Completate */}
                <div>
                  <div 
                    className="bg-emerald-50 p-3 flex items-center justify-between cursor-pointer hover:bg-emerald-100/50 transition-colors"
                    onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                    data-testid="completed-tasks-accordion"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-emerald-600 transition-transform ${showCompletedTasks ? 'rotate-180' : ''}`} />
                      <span className="font-semibold text-emerald-800">
                        Task completate ({personalTasks.filter(t => t.status === "completato").length})
                      </span>
                    </div>
                  </div>
                  
                  {showCompletedTasks && (
                    <div className="p-3 space-y-2 bg-emerald-50/30">
                      {personalTasks.filter(t => t.status === "completato").length > 0 ? (
                        personalTasks
                          .filter(t => t.status === "completato")
                          .sort((a, b) => new Date(b.completed_at || b.due_date) - new Date(a.completed_at || a.due_date))
                          .map(task => (
                          <div 
                            key={task.task_id}
                            className="p-3 border rounded-lg bg-white border-emerald-200"
                            data-testid={`personal-task-${task.task_id}`}
                          >
                            {/* Badge Cliente in grassetto */}
                            <div className="mb-1">
                              <Badge className="bg-indigo-100 text-indigo-800 font-bold text-xs">
                                {task.client_name || "Cliente non assegnato"}
                              </Badge>
                            </div>
                            <div className="flex items-start justify-between">
                              <p className="font-medium line-through opacity-70 flex-1">{task.title}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 shrink-0"
                                onClick={(e) => { e.stopPropagation(); setEditingTask(task); setShowEditDialog(true); }}
                                data-testid={`edit-completed-task-${task.task_id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(task.due_date).toLocaleDateString("it-IT")}
                              </Badge>
                              {task.smm_name && (
                                <Badge variant="secondary" className="text-xs">
                                  {task.smm_name}
                                </Badge>
                              )}
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                ✓ Completato
                              </Badge>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nessuna task completata
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessuna task personale</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="search-input"
                  placeholder="Cerca cliente o task..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterSemaforo} onValueChange={setFilterSemaforo}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-semaforo">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Semaforo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="verde">Verde</SelectItem>
                  <SelectItem value="giallo">Giallo</SelectItem>
                  <SelectItem value="rosso">Rosso</SelectItem>
                  <SelectItem value="scaduto">Scaduto</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="da_fare">Da fare</SelectItem>
                  <SelectItem value="in_corso">In corso</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <TaskList 
              tasks={filteredTasks} 
              onStatusChange={updateTaskStatus}
              getSemaforoBadge={getSemaforoBadge}
              navigate={navigate}
              showStatusButtons={true}
            />
          </TabsContent>
        </Tabs>
      )}
      </>
      )}

      {/* Edit Personal Task Dialog */}
      <PersonalTaskEditDialog
        task={editingTask}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSaved={fetchData}
      />
    </div>
  );
}

// Task List Component
function TaskList({ tasks, onStatusChange, getSemaforoBadge, navigate, showStatusButtons = false }) {
  if (tasks.length === 0) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Nessun task trovato</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-slate-200">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Cliente
              </th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Task
              </th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Scadenza
              </th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Stato
              </th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, index) => (
              <tr 
                key={task.task_id} 
                className="border-b border-slate-100 table-row-hover cursor-pointer"
                onClick={() => navigate(`/task/${task.task_id}`)}
                data-testid={`task-row-${index}`}
              >
                <td className="p-4">
                  <span className="font-medium text-foreground">{task.client_name}</span>
                </td>
                <td className="p-4 text-muted-foreground">
                  {etichettaTask(task, TASK_TYPE_LABELS)}
                </td>
                <td className="p-4 text-muted-foreground">
                  {new Date(task.due_date).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  })}
                </td>
                <td className="p-4">
                  {getSemaforoBadge(task.semaforo, task.status)}
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  {showStatusButtons && task.status !== "completato" && (
                    <div className="flex gap-1">
                      {task.status === "da_fare" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => onStatusChange(task.task_id, "in_corso")}
                          data-testid={`inprogress-btn-${index}`}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          In corso
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                        onClick={() => onStatusChange(task.task_id, "completato")}
                        data-testid={`complete-btn-${index}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Completa
                      </Button>
                    </div>
                  )}
                  {task.status === "in_corso" && (
                    <div className="flex gap-1">
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">In corso</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                        onClick={() => onStatusChange(task.task_id, "completato")}
                        data-testid={`complete-btn-${index}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Completa
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
